import React, { useEffect, useState, useRef, useMemo } from "react";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useSelector } from "react-redux";
// CUSTOM HOOKS
import usePostCommand from "../../../customHooks/POST_requests/usePostCommand";
import useGetAllMessages from "../../../customHooks/GET_requests/useGetAllMessages";
// COMPONENTS
import ActionCommandButton from "../Actions/components/ActionCommandButton";
import GlobalAlert2 from "../../home/GlobalAlert2";
// INTERFACES
import { RootState } from "../../../app/store";
import { ActionCommandOption } from "../../../interfaces/Bank.interface";
// CONSTANTS
import {
  resolveCommandEnum,
  resolveValidStateEnums,
  assertBoundCommand,
  UNBOUND_COMMAND_REASON,
  dispatchFloor,
  floorSelectionMissing,
} from "./actionButtonUtils";
import useAwaitingTargetReason from "./useAwaitingTargetReason";
import { LiftType } from "../../../flatbuffers/generated/archive/lift-type";
// VERIFICATION
import useActionOutcome, { ActionVerification } from "./useActionOutcome";

function ActionButtonSwitch({
  validStates,
  toggleValidStates,
  label,
  toggleLabel,
  messageId,
  toggleMessageId,
  command = null,
  toggleCommand = null,
  colorAction,
  timerAction,
  carService,
  targetFloor = undefined,
  expectedResponse = null,
  expectedResponseTimeout = null,
  commandMessageId = null,
}: {
  validStates: string[];
  toggleValidStates: string[];
  label: string;
  toggleLabel: string;
  messageId: string;
  toggleMessageId: string;
  command?: ActionCommandOption | null;
  toggleCommand?: ActionCommandOption | null;
  colorAction: string;
  timerAction: number;
  carService: number;
  /**
   * The floor this row's commands dispatch to. `undefined` for a command that
   * takes no floor (dispatches at 0, as before); `null` while a floor-taking
   * command is still waiting for the operator to pick one. See dispatchFloor.
   */
  targetFloor?: number | null;
} & ActionVerification) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const [t] = useTranslation("global");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [localCarService, setLocalCarService] = useState<number>(0);
  const [lastCommandSent, setLastCommandSent] = useState<string>("ON");

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { theme } = useSelector((state: RootState) => state.home);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const { triggerPostCommand, isLoadingPostCommand, isErrorPostCommand } = usePostCommand();
  const { messages } = useGetAllMessages();
  const reportOutcome = useActionOutcome({
    expectedResponse,
    expectedResponseTimeout,
    commandMessageId,
  });

  // ************************************************ */
  // MEMOIZED VALUES ******************************** */
  // null (not 0) when the message is unbound — enum 0 is a real Control
  // (CarCallLock), so a 0 fallback would mis-fire that command.
  const messageEnum = useMemo(
    () => resolveCommandEnum(messages, messageId, command),
    [messages, messageId, command],
  );

  const toggleMessageEnum = useMemo(
    () => resolveCommandEnum(messages, toggleMessageId, toggleCommand),
    [messages, toggleMessageId, toggleCommand],
  );

  const validStatesNumbers = useMemo(
    () => resolveValidStateEnums(messages, validStates),
    [messages, validStates],
  );

  const toggleValidStatesNumbers = useMemo(
    () => resolveValidStateEnums(messages, toggleValidStates),
    [messages, toggleValidStates],
  );

  // ************************************************ */
  // FUNCTIONS ************************************** */
  // Send the action's BOUND command, never a hardcoded Control value.
  const onClickToggleOn = async () => {
    if (!assertBoundCommand(messageEnum, label, theme)) {
      reportOutcome(label, false, messageId);
      return;
    }
    setLastCommandSent("ON");
    const delivered = await triggerPostCommand({
      floor: dispatchFloor(targetFloor),
      command: messageEnum,
      liftType: LiftType[LiftType.ELEVATOR] as "ELEVATOR",
      active: true,
    });
    reportOutcome(label, delivered, messageId);
  };

  const onClickToggleOff = async () => {
    if (!assertBoundCommand(toggleMessageEnum, toggleLabel, theme)) {
      reportOutcome(toggleLabel, false, toggleMessageId ?? null);
      return;
    }
    setLastCommandSent("OFF");
    const delivered = await triggerPostCommand({
      floor: dispatchFloor(targetFloor),
      command: toggleMessageEnum,
      liftType: LiftType[LiftType.ELEVATOR] as "ELEVATOR",
      active: true,
    });
    reportOutcome(toggleLabel, delivered, toggleMessageId ?? null);
  };

  // Pending delayed-off sequence. Tracked so cancel/unmount can clear it —
  // otherwise the scheduled off fires after a manual cancel (double send)
  // or after the panel closes.
  const offTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearPendingOff = () => {
    if (offTimeoutRef.current !== null) {
      clearTimeout(offTimeoutRef.current);
      offTimeoutRef.current = null;
    }
  };

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    setLocalCarService(carService);
  }, [carService]);

  // Clear any in-flight delayed-off when the panel unmounts.
  useEffect(() => clearPendingOff, []);

  // ************************************************ */
  // COMPONENT ************************************** */
  // Not a Toggle: the primary runs a SEQUENCE (primary command, then the paired
  // command after the configured delay) and the secondary is a cancel that also
  // clears the pending step. Both halves of that sequence are unchanged; the
  // secondary is styled as an outlined danger control so the cancel intent reads
  // from shape and label, not from a colour the operator configured.
  const primaryBlocked = validStatesNumbers.includes(localCarService);
  const secondaryBlocked = toggleValidStatesNumbers.includes(localCarService);
  const unavailableReason = t(
    "monitoringActions.unavailableInCurrentState",
    "Unavailable in the current service mode",
  );

  // A floor-taking command must not dispatch until the operator picks a floor
  // on the row's strip; `targetFloor` is null for exactly as long as that is
  // true. The gate is VISIBLE — a disabled button naming its reason — rather
  // than a click that silently sends nothing, and rather than the position-0
  // dispatch it replaces, which aimed a positional command at a floor the
  // operator never chose.
  const awaitingFloor = floorSelectionMissing(targetFloor);
  const selectFloorReason = useAwaitingTargetReason(messageEnum);
  // A function rather than a ternary at each call site: the floor gate outranks
  // the service-state gate, and both have to reach one tooltip slot without
  // nesting a conditional inside a conditional.
  const reasonFor = (stateBlocked: boolean, commandEnum: number | null): string | undefined => {
    // Ahead of the transient gates: a missing binding is a configuration
    // fault that no amount of waiting or floor-picking will clear, so it is
    // the reason worth showing.
    if (commandEnum == null) return UNBOUND_COMMAND_REASON;
    if (awaitingFloor) return selectFloorReason;
    if (stateBlocked) return unavailableReason;
    return undefined;
  };

  return (
    <>
      <ActionCommandButton
        label={label}
        color={colorAction}
        disabled={primaryBlocked || awaitingFloor || isLoadingPostCommand || messageEnum == null}
        loading={isLoadingPostCommand && lastCommandSent === "ON"}
        disabledReason={reasonFor(primaryBlocked, messageEnum)}
        onClick={() => {
          onClickToggleOn();
          // The sequence is unconditional: a SWITCH is primary → delay → paired
          // command by definition, so the second step is always scheduled and
          // assertBoundCommand reports a row whose paired command is missing
          // rather than the sequence silently ending after step one. The
          // `|| 1000` preserves the legacy 1s pulse for rows saved with timer 0.
          clearPendingOff();
          offTimeoutRef.current = setTimeout(
            () => {
              offTimeoutRef.current = null;
              onClickToggleOff();
            },
            timerAction * 1000 || 1000,
          );
        }}
      />
      {/* Always rendered: the cancel control is what stops the pending step
          above, so it must exist whenever that step can be scheduled. */}
      <ActionCommandButton
        label={toggleLabel || t("monitoringActions.cancel", "CANCEL")}
        outlined
        danger
        disabled={
          secondaryBlocked || awaitingFloor || isLoadingPostCommand || toggleMessageEnum == null
        }
        loading={isLoadingPostCommand && lastCommandSent === "OFF"}
        disabledReason={reasonFor(secondaryBlocked, toggleMessageEnum)}
        onClick={() => {
          clearPendingOff();
          onClickToggleOff();
        }}
      />
      {/** ---------------------------------------------------- */}
      {/** GLOBAL ALERT */}
      <GlobalAlert2
        isError={isErrorPostCommand}
        isSuccess={false}
        requestType="POST"
        error="Error"
        name="Command"
      />
    </>
  );
}

export default ActionButtonSwitch;
